import { useEffect, useState } from "react";
import { Building2, FileText, MessageSquare, ShieldCheck, Star, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { api, apiError, fmtDate } from "../lib/api";
import WhiteboardBanner from "../components/WhiteboardBanner";


/**
 * Client portal — clean, professional, read-only, single-site.
 *
 * Nothing gamified, no confetti, no streaks — this is what an external
 * client sees when they log in. Shows: site header, service-request summary,
 * compliance status chips, published monthly reports archive, and a link
 * to rate the current month.
 */
export default function ClientDashboardPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        try {
            const { data } = await api.get("/client/dashboard");
            setData(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    if (loading || !data) {
        return <div className="mx-auto max-w-5xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;
    }

    const site = data.site;
    const sr = data.service_requests;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10" data-testid="client-dashboard">
            <header className="mb-8">
                <div className="text-xs font-medium uppercase tracking-widest text-[#E02B2B]">Client portal · Flexworx Ops</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E3355] mt-2 flex items-center gap-3">
                    <Building2 className="w-8 h-8" style={{ color: site.accent_color || "#1E3355" }} />
                    {site.name}
                </h1>
                {site.address && <p className="text-sm text-gray-500 mt-1">{site.address}</p>}
            </header>

            <WhiteboardBanner />

            {/* Service request summary */}
            <section className="mb-8" data-testid="client-sr-summary">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold uppercase tracking-widest text-[#1E3355] inline-flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Service requests
                    </h2>
                    <button
                        onClick={() => {
                            const category = window.prompt("Category (e.g. Cleaning, Reception, Facilities):", "General");
                            if (!category) return;
                            const description = window.prompt("Describe your request in one or two lines:");
                            if (!description) return;
                            api.post("/client/service-requests", { category, description })
                                .then(() => { toast.success("Request raised. HO has been notified."); load(); })
                                .catch((e) => toast.error(apiError(e)));
                        }}
                        className="inline-flex items-center gap-1 rounded-full bg-[#E02B2B] text-white text-xs px-3 py-1.5 hover:bg-red-700"
                        data-testid="client-raise-sr-btn"
                    >
                        + Raise a request
                    </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <SummaryCard label="Open" value={sr.open} tone="red" testid="sr-open" />
                    <SummaryCard label="Closed" value={sr.closed} tone="green" testid="sr-closed" />
                    <SummaryCard label="Total" value={sr.total} testid="sr-total" />
                    <SummaryCard label="Avg response" value={sr.avg_response_days != null ? `${sr.avg_response_days} d` : "—"} testid="sr-avg" />
                </div>
                {sr.recent.length > 0 ? (
                    <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white overflow-hidden">
                        {sr.recent.map((r, i) => (
                            <li key={i} className="p-3 flex items-center justify-between" data-testid={`sr-recent-${i}`}>
                                <div className="min-w-0">
                                    <div className="font-medium text-[#1E3355] truncate">{r.description}</div>
                                    <div className="text-xs text-gray-500">{fmtDate(r.date)}</div>
                                </div>
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "Open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>{r.status}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-500 text-center">No recent requests.</div>
                )}
            </section>

            {/* Compliance chips */}
            <section className="mb-8" data-testid="client-compliance">
                <h2 className="text-sm font-bold uppercase tracking-widest text-[#1E3355] mb-3 inline-flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> Compliance status
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {data.compliance.map((c) => (
                        <ComplianceChip key={c.task} label={c.label || `Task ${c.task}`} status={c.status} due={c.due_date} testid={`compliance-${c.task}`} />
                    ))}
                </div>
            </section>

            {/* Reports */}
            <section className="mb-8" data-testid="client-reports">
                <h2 className="text-sm font-bold uppercase tracking-widest text-[#1E3355] mb-3 inline-flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Published monthly reports
                </h2>
                {data.reports.length === 0 ? (
                    <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-500 text-center">No reports published yet.</div>
                ) : (
                    <ul className="rounded-xl border border-gray-100 bg-white divide-y divide-gray-100 overflow-hidden">
                        {data.reports.map((r) => (
                            <li key={r.month} className="p-3 flex items-center justify-between" data-testid={`report-${r.month}`}>
                                <div>
                                    <div className="font-medium text-[#1E3355]">{r.month}</div>
                                    <div className="text-xs text-gray-500">{r.status === "SIGNED_OFF" ? "Signed off" : "Published"}{r.published_at ? ` · ${fmtDate(r.published_at)}` : ""}</div>
                                </div>
                                {r.public_token && (
                                    <a href={`/r/${r.public_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3355] hover:underline" data-testid={`open-report-${r.month}`}>
                                        Open <ArrowUpRight className="w-3.5 h-3.5" />
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Rate this month */}
            <section className="mb-4" data-testid="client-rating-cta">
                <div className="rounded-xl border border-[#E02B2B]/20 bg-[#FFF5F5] p-4 flex items-center gap-4">
                    <Star className="w-6 h-6 text-[#E02B2B]" />
                    <div className="flex-1">
                        <div className="font-bold text-[#1E3355]">Rate this month</div>
                        <div className="text-xs text-gray-600">Tell Flexworx how we performed at {site.name} in {data.rating_link.month}. Your feedback is filed with the monthly report.</div>
                    </div>
                    <a
                        href="#"
                        onClick={async (e) => {
                            e.preventDefault();
                            try {
                                const { data } = await api.get(`/public/link-preview/rating/${site.id}/${data?.rating_link?.month || site.id}`);
                                window.open(data.path, "_blank");
                            } catch (err) {
                                // Fall back: build ourselves via the /r page (Owner's team can share direct link)
                                toast.info("Please ask your Flexworx contact for the rating link.");
                            }
                        }}
                        className="px-3 py-1.5 rounded-md bg-[#E02B2B] hover:bg-[#B31E1E] text-white text-sm font-semibold"
                        data-testid="rate-month-btn"
                    >Rate</a>
                </div>
            </section>

            <div className="text-[11px] text-gray-400 text-center mt-8">
                Flexworx Ops · A Flexworx Company · this portal is read-only.
            </div>
        </div>
    );
}


function SummaryCard({ label, value, tone, testid }) {
    const cls = tone === "red" ? "text-red-700" : tone === "green" ? "text-emerald-700" : "text-[#1E3355]";
    return (
        <div className="rounded-xl border border-gray-100 bg-white p-3" data-testid={testid}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{label}</div>
            <div className={`font-display text-2xl font-bold mt-1 ${cls}`}>{value}</div>
        </div>
    );
}


function ComplianceChip({ label, status, due, testid }) {
    const map = {
        "OK": "bg-emerald-100 text-emerald-700",
        "DUE_SOON": "bg-amber-100 text-amber-800",
        "OVERDUE": "bg-red-100 text-red-700",
    };
    const cls = map[status] || "bg-gray-100 text-gray-600";
    const displayStatus = status || "—";
    return (
        <div className="rounded-xl border border-gray-100 bg-white p-3" data-testid={testid}>
            <div className="text-xs font-semibold text-[#1E3355] truncate">{label}</div>
            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{displayStatus.replace("_", " ")}</span>
            {due && <div className="text-[10px] text-gray-500 mt-1">Due {fmtDate(due)}</div>}
        </div>
    );
}
